import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Copy, Check, FlaskConical } from "lucide-react";

import { api } from "@/lib/api";
import IntakeHeader from "@/components/intake/IntakeHeader";
import StepFooter from "@/components/intake/StepFooter";
import StepPersonal, { validatePersonal } from "@/components/intake/StepPersonal";
import StepDocuments from "@/components/intake/StepDocuments";
import StepDedicatedEmail, { validateDedicatedEmail } from "@/components/intake/StepDedicatedEmail";
import StepBureauLogin, { validateBureauLogin } from "@/components/intake/StepBureauLogin";
import StepFinalize, { validateFinalize } from "@/components/intake/StepFinalize";

const FIRST_STEP = 2;
const LAST_STEP = 6;

// Generate a random valid-looking SSN to satisfy the backend submit-once rule
// when dev mode skips validation. Avoids hitting the unique identity_hash index.
function randomSSN() {
    const a = String(100 + Math.floor(Math.random() * 800)).padStart(3, "0");
    const b = String(10 + Math.floor(Math.random() * 89)).padStart(2, "0");
    const c = String(1000 + Math.floor(Math.random() * 8999)).padStart(4, "0");
    return `${a}-${b}-${c}`;
}
function todayMMDDYYYY() {
    const d = new Date();
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear() - 30}`;
}

export default function IntakeFlow() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || localStorage.getItem("aurum_resume_token");
    const devMode = params.get("dev") === "1";

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [step, setStep] = useState(FIRST_STEP);
    const [stepData, setStepData] = useState({
        personal: {},
        dedicated_email: {},
        bureau_login: {},
        finalize: {},
    });
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState({});
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!token) {
            toast.error("No active application — start fresh.");
            navigate("/");
            return;
        }
        (async () => {
            try {
                const { data } = await api.get(`/intake/draft/${token}`);
                localStorage.setItem("aurum_resume_token", token);
                const sd = data.step_data || {};
                setStepData({
                    personal: sd.personal || {},
                    dedicated_email: sd.dedicated_email || {},
                    bureau_login: sd.bureau_login || {},
                    finalize: sd.finalize || {},
                });
                setFiles((data.uploaded_files || []).filter((f) => !f.is_deleted));
                setStep(Math.max(FIRST_STEP, Math.min(LAST_STEP, data.current_step || FIRST_STEP)));
            } catch (e) {
                if (e?.response?.status === 410) {
                    toast.info("This application has already been submitted.");
                    navigate("/");
                } else {
                    toast.error("Could not load your application.");
                    navigate("/");
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [token, navigate]);

    const update = (k) => (v) => setStepData((s) => ({ ...s, [k]: v }));

    const persist = async (nextStep, dataOverride) => {
        await api.put(`/intake/draft/${token}`, {
            current_step: nextStep,
            step_data: dataOverride || stepData,
        });
    };

    const validateCurrent = () => {
        if (devMode) return {};
        if (step === 2) return validatePersonal(stepData.personal);
        if (step === 4) return validateDedicatedEmail(stepData.dedicated_email);
        if (step === 5) return validateBureauLogin(stepData.bureau_login);
        if (step === 6) return validateFinalize(stepData.finalize);
        return {};
    };

    const next = async () => {
        const errs = validateCurrent();
        if (Object.keys(errs).length) {
            setErrors(errs);
            toast.error("Please complete the highlighted fields.");
            return;
        }
        setErrors({});
        const nextStep = Math.min(LAST_STEP, step + 1);
        try {
            await persist(nextStep);
            setStep(nextStep);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
            toast.error("Could not save. Please try again.");
        }
    };

    const back = async () => {
        const prev = Math.max(FIRST_STEP, step - 1);
        try { await persist(prev); } catch {}
        setStep(prev);
        setErrors({});
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const saveAndExit = async () => {
        setSaving(true);
        try { await persist(step); setShowSaveModal(true); }
        catch { toast.error("Could not save."); }
        finally { setSaving(false); }
    };

    const submit = async () => {
        let working = stepData;
        if (devMode) {
            // Fill the minimum bits needed by the backend (SSN + DOB) so submit-once works
            const p = { ...(working.personal || {}) };
            if (!p.ssn) p.ssn = randomSSN();
            if (!p.dob) p.dob = todayMMDDYYYY();
            if (!p.first_name) p.first_name = "Dev";
            if (!p.last_name) p.last_name = "Tester";
            if (!p.email) p.email = `dev-${Date.now()}@bsg.test`;
            if (!p.mobile) p.mobile = "555-555-1212";
            working = { ...working, personal: p };
        } else {
            const allErrs = {
                ...validatePersonal(stepData.personal),
                ...validateDedicatedEmail(stepData.dedicated_email),
                ...validateFinalize(stepData.finalize),
            };
            if (Object.keys(allErrs).length) {
                setErrors(allErrs);
                toast.error("Please complete all required fields before submitting.");
                return;
            }
        }
        setSubmitting(true);
        try {
            await persist(LAST_STEP, working);
            const { data } = await api.post(`/intake/submit/${token}`);
            localStorage.removeItem("aurum_resume_token");
            navigate("/confirmation", { state: { submissionId: data.submission_id } });
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(detail || "Could not submit. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const resumeUrl = `${window.location.origin}/intake?token=${token}`;
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(resumeUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {}
    };

    if (loading) {
        return (
            <div className="intake-shell flex items-center justify-center" data-testid="intake-loading">
                <Loader2 className="animate-spin text-[#D4AF37]" size={28} />
            </div>
        );
    }

    return (
        <div className="intake-shell pb-12" data-testid="intake-page">
            <IntakeHeader currentStep={step} totalSteps={LAST_STEP} />

            {devMode && (
                <div
                    className="mx-auto max-w-3xl mt-3 px-4 py-2 rounded-md text-xs flex items-center gap-2 border border-[#D4AF37]/40 bg-[#D4AF37]/10"
                    data-testid="dev-mode-banner"
                >
                    <FlaskConical size={14} className="text-[#D4AF37]" />
                    <span className="text-white/85">
                        <span className="gold-text font-semibold">DEV MODE</span> — validation skipped. Submit will auto-fill required fields.
                    </span>
                </div>
            )}

            <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-8">
                <div className="intake-card p-7 md:p-10 animate-fade-up" key={step}>
                    {step === 2 && (
                        <StepPersonal value={stepData.personal} onChange={update("personal")} errors={errors} />
                    )}
                    {step === 3 && (
                        <StepDocuments token={token} files={files} onFilesChange={setFiles} />
                    )}
                    {step === 4 && (
                        <StepDedicatedEmail value={stepData.dedicated_email} onChange={update("dedicated_email")} errors={errors} />
                    )}
                    {step === 5 && (
                        <StepBureauLogin
                            value={stepData.bureau_login}
                            onChange={update("bureau_login")}
                            defaultUsername={stepData.dedicated_email?.new_email}
                        />
                    )}
                    {step === 6 && (
                        <StepFinalize value={stepData.finalize} onChange={update("finalize")} errors={errors} />
                    )}

                    <StepFooter
                        onBack={back}
                        onNext={step < LAST_STEP ? next : submit}
                        onSave={saveAndExit}
                        backDisabled={step === FIRST_STEP}
                        saving={saving}
                        submitting={submitting}
                        submitMode={step === LAST_STEP}
                    />
                </div>

                {step === 4 && (
                    <div className="mt-6 flex items-center justify-center gap-2 text-xs text-white/55" data-testid="security-notice">
                        <span className="w-2.5 h-2.5 rounded-full border border-[#D4AF37]/60" />
                        Your information is transmitted securely and encrypted in transit.
                    </div>
                )}
                {step === 6 && (
                    <div className="mt-6 text-center text-sm font-medium" data-testid="submit-notice">
                        <span className="text-[#2BB673]">Secure submission — you're all set.</span>
                    </div>
                )}
            </main>

            {showSaveModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
                    role="dialog"
                    data-testid="save-modal"
                >
                    <div className="intake-card max-w-md w-full p-8">
                        <h3 className="font-display text-2xl text-white">Saved.</h3>
                        <p className="mt-2 text-sm text-white/65">
                            Keep this resume token somewhere safe. Paste it on the resume page or use the link below to continue later.
                        </p>
                        <div className="mt-5">
                            <label className="field-label block mb-2">Resume token</label>
                            <div className="flex items-stretch gap-2">
                                <input
                                    readOnly
                                    value={token}
                                    onClick={(e) => e.target.select()}
                                    className="glow-input flex-1 px-3 py-2.5 rounded-xl text-xs font-mono"
                                    data-testid="save-modal-token"
                                />
                                <button onClick={copy} className="btn-gold px-3" data-testid="save-modal-copy-btn">
                                    {copied ? <Check size={14} /> : <Copy size={14} />}
                                </button>
                            </div>
                            <p className="text-[11px] text-white/45 mt-2 break-all">{resumeUrl}</p>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button onClick={() => setShowSaveModal(false)} className="btn-ghost-light px-4 py-2 text-sm" data-testid="save-modal-close-btn">
                                Keep editing
                            </button>
                            <button onClick={() => navigate("/")} className="btn-gold px-4 py-2 text-sm" data-testid="save-modal-exit-btn">
                                Exit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
